import { Injectable } from '@angular/core';
import { map, tap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, Subject } from 'rxjs';
import { SecureHttp } from '../helpers/securehttp';
import { SurplusLinesApplicant, SurplusLinesOpportunitySnapshot } from '../models/surplus-lines-opportunity-snapshot.model';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { environment } from '../../environments/environment';
import { SurplusLinesPlanDesign, SurplusLinesPlanDesignsRequest } from '../models/surplus-lines-plan-design.model';
import { CompassQuoteOptions, EmailCodeResult, SurplusLinesOpportunityDetail } from '../models/surplus-lines-opportunity.model';
import { QuestionnaireService } from './questionnaire.service';
import { QuestionnaireQuestionModel } from '../models/questionnaire-question.model';

export interface AdditionalBenefitsState {
  COLA: number; COLADuration: string; COLACost: string;
  IDR: number; IDRDuration: string; IDRCost: string;
  EEB: number; EEBDuration: string; EEBCost: string;
  SLR: number; SLRDuration: string; SLRAmount: string; SLRCost: string;
  PLP: number; PLPDuration: string; PLPAmount: string; PLPCost: string;
  RCB: number; RCBDuration: string; RCBAmount: string; RCBCost: string;
  eliminationPeriod: string;
  monthlyBenefit: string;
  benefitDuration: string;
}

@Injectable({
  providedIn: 'root'
})
export class OpportunitySnapshotsService {
  snackBarConfig: MatSnackBarConfig = {
    horizontalPosition: 'center',
    verticalPosition: 'top',
    duration: 5000
  };

  private _updateQueue: SurplusLinesOpportunitySnapshot[] = [];
  private _updating = false;
  private updateSubject = new Subject<SurplusLinesOpportunitySnapshot>();

  private _benefitsByOpp = new Map<number, AdditionalBenefitsState>();
  private benefitsKey = (opportunityId: number) => `hl_addl_${opportunityId}`;

  saveAdditionalBenefits(opportunityId: number, state: AdditionalBenefitsState): void {
    if (opportunityId == null) return;
    this._benefitsByOpp.set(opportunityId, state);
    try {
      sessionStorage.setItem(this.benefitsKey(opportunityId), JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }

  loadAdditionalBenefits(opportunityId: number): AdditionalBenefitsState | null {
    if (opportunityId == null) return null;
    const cached = this._benefitsByOpp.get(opportunityId);
    if (cached) return cached;

    const raw = sessionStorage.getItem(this.benefitsKey(opportunityId));
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as AdditionalBenefitsState;
      this._benefitsByOpp.set(opportunityId, parsed);
      return parsed;
    } catch {
      return null;
    }
  }

  clearAdditionalBenefits(opportunityId: number): void {
    if (opportunityId == null) return;
    this._benefitsByOpp.delete(opportunityId);
    try {
      sessionStorage.removeItem(this.benefitsKey(opportunityId));
    } catch {
      // ignore storage errors
    }
  }

  clearAllAdditionalBenefits(): void {
    this._benefitsByOpp.clear();
    try {
      const toDelete: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('hl_addl_')) toDelete.push(k);
      }
      toDelete.forEach(k => sessionStorage.removeItem(k));
    } catch {
      // ignore storage errors
    }
  }
  // -------------------------------------------------------------------------

  constructor(
    private _http: HttpClient,
    private _https: SecureHttp,
    private _snackbar: MatSnackBar,
    private _questionnaireService: QuestionnaireService
  ) {
    this.updateSubject.pipe(
      debounceTime(300),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ).subscribe(snapshot => this.queueUpdate(snapshot));
  }

  get currentOpportunitySnapshot(): SurplusLinesOpportunitySnapshot {
    return JSON.parse(localStorage.getItem('OpportunitySnapshot') as string) as SurplusLinesOpportunitySnapshot;
  }

  get currentQuote(): SurplusLinesPlanDesign[] {
    return JSON.parse(localStorage.getItem('Quote') as string) as SurplusLinesPlanDesign[];
  }

  get currentApplicant(): SurplusLinesApplicant {
    return JSON.parse(localStorage.getItem('ApplicantInfo') as string) as SurplusLinesApplicant;
  }

  get currentCompassOpportunity(): SurplusLinesOpportunityDetail | undefined {
    return JSON.parse(localStorage.getItem('CompassOpportunity') as string) as SurplusLinesOpportunityDetail;
  }

  get currentQuoteOptions(): CompassQuoteOptions | undefined {
    return JSON.parse(localStorage.getItem('QuoteOptions') as string) as CompassQuoteOptions;
  }

  get CurrentUser(): any | undefined {
    return JSON.parse(localStorage.getItem('User') as string);
  }

  get CurrentUserId(): any | undefined {
    return localStorage.getItem('UserId') as string;
  }

  get CurrentEmailCode(): EmailCodeResult | undefined {
    if (!localStorage.getItem('EmailCode')) {
      return JSON.parse(localStorage.getItem('EmailCode') as string);
    } else {
      return undefined;
    }
  }

  getSpecialtyCodes() {
    const url = `${environment.apiUrl}api/surplus-lines/reference`;
    return this._http.get(url).pipe(map(response => response as any));
  }

  current(opportunityId: number | string): Observable<SurplusLinesOpportunitySnapshot> {
    const url = `${environment.apiUrl}api/surplus-lines/snapshots/current`;
    const params = new HttpParams().set('opportunityId', opportunityId.toString());

    return forkJoin({
      OppSnapShot: this._https.get(url, { params }),
      QuestionModel: this._questionnaireService.getQuestionnaireQuestions()
    }).pipe(
      map(({ OppSnapShot, QuestionModel }) => {
        const snapshot = OppSnapShot as SurplusLinesOpportunitySnapshot;
        const questionModel = QuestionModel as QuestionnaireQuestionModel[];
        snapshot.Questions.forEach(x => {
          x.QuestionText = questionModel.find(y => y.QuestionNum === x.QuestionNum)?.QuestionText || '';
        });
        return snapshot;
      }),
      tap(response => {
        localStorage.setItem('OpportunitySnapshot', JSON.stringify(response));
      })
    );
  }

  currentOppSnapByApplicantId(ApplicantIdId: number | string, Token: string): Observable<SurplusLinesOpportunitySnapshot> {
    const url = `${environment.apiUrl}api/surplus-lines/snapshots/currentByApplicantId`;
    Token == null ? '' : Token.toLowerCase();
    const params = new HttpParams()
      .set('applicantId', ApplicantIdId.toString())
      .set('token', Token);

    return forkJoin({
      OppSnapShot: this._https.get(url, { params }),
      QuestionModel: this._questionnaireService.getQuestionnaireQuestions()
    }).pipe(
      map(({ OppSnapShot, QuestionModel }) => {
        const snapshot = OppSnapShot as SurplusLinesOpportunitySnapshot;
        const questionModel = QuestionModel as QuestionnaireQuestionModel[];
        snapshot.Questions.forEach(x => {
          x.QuestionText = questionModel.find(y => y.QuestionNum === x.QuestionNum)?.QuestionText || '';
        });
        return snapshot;
      }),
      tap(response => {
        localStorage.setItem('OpportunitySnapshot', JSON.stringify(response));
      })
    );
  }

  create(snapshot: SurplusLinesOpportunitySnapshot): Observable<SurplusLinesOpportunitySnapshot> {
    const url = `${environment.apiUrl}api/surplus-lines/snapshots`;
    return this._https.post(url, snapshot).pipe(
      tap(response => localStorage.setItem('OpportunitySnapshot', JSON.stringify(response))),
      map(response => response as SurplusLinesOpportunitySnapshot)
    );
  }

  update(snapshot: SurplusLinesOpportunitySnapshot) {
    snapshot.PaymentPlanId = +snapshot.PaymentPlanId;
    this.updateSubject.next(snapshot);
  }

  private queueUpdate(snapshot: SurplusLinesOpportunitySnapshot) {
    this._updateQueue = [snapshot];
    if (!this._updating) {
      this.sendUpdate();
    }
  }

  private sendUpdate() {
    const snapshot = this._updateQueue.shift();
    if (!snapshot) return;

    const url = `${environment.apiUrl}api/surplus-lines/snapshots`;
    this._updating = true;

    forkJoin({
      OppSnapShot: this._https.put(url, snapshot),
      QuestionModel: this._questionnaireService.getQuestionnaireQuestions()
    }).pipe(
      map(({ OppSnapShot, QuestionModel }) => {
        const snap = OppSnapShot as SurplusLinesOpportunitySnapshot;
        const questionModel = QuestionModel as QuestionnaireQuestionModel[];

        snap.Questions.forEach(x => {
          x.QuestionText = questionModel.find(y => y.QuestionNum === x.QuestionNum)?.QuestionText || '';
        });
        return snap;
      }),
      tap(response => {
        localStorage.setItem('OpportunitySnapshot', JSON.stringify(response));
      })
    ).subscribe({
      next: () => {},
      error: (err) => { return err; },
      complete: () => {
        this._updating = false;
        if (this._updateQueue.length) {
          this.sendUpdate();
        }
      }
    });
  }

  updateApplicantInfo(applicantInfo: SurplusLinesApplicant): Observable<SurplusLinesApplicant> {
    const url = `${environment.apiUrl}api/surplus-lines/update/applicant`;
    return this._https.put(url, applicantInfo).pipe(
      tap(response => localStorage.setItem('ApplicantInfo', JSON.stringify(response))),
      map(response => response as SurplusLinesApplicant)
    );
  }

  addOpportunitySnapshot(opportunitySnapshot: SurplusLinesOpportunitySnapshot): Observable<SurplusLinesOpportunitySnapshot> {
    const url = `${environment.apiUrl}api/surplus-lines/snapshots`;
    return this._https.post(url, opportunitySnapshot).pipe(
      tap(response => localStorage.setItem('OpportunitySnapshot', JSON.stringify(response))),
      map(response => response as SurplusLinesOpportunitySnapshot)
    );
  }

  getQuote(opportunityId: number, model: SurplusLinesPlanDesignsRequest): Observable<SurplusLinesPlanDesign[]> {
    const url = `${environment.apiUrl}api/surplus-lines/opportunities/${opportunityId}/plan-designs`;
    return this._https.post(url, model).pipe(
      tap(response => localStorage.setItem('Quote', JSON.stringify(response))),
      tap(() => this.saveReturningUser()),
      map(response => response as SurplusLinesPlanDesign[])
    );
  }

  getExistingQuoteOptions(opportunityId: number, InsuredId: number, Model: SurplusLinesApplicant, PlanDesign: SurplusLinesPlanDesignsRequest): Observable<SurplusLinesPlanDesign[]> {
    const url = `${environment.apiUrl}api/surplus-lines/opportunities/${opportunityId}/plan-designs/latest`;
    const body = { InsuredId, Model, PlanDesign };

    return this._https.post(url, body ).pipe(
      tap(response => localStorage.setItem('Quote', JSON.stringify(response))),
      tap(() => this.saveReturningUser()),
      map(response => response as SurplusLinesPlanDesign[])
    );
  }

  getCompassOpportunity(): Observable<SurplusLinesOpportunityDetail> {
    const url = `${environment.apiUrl}api/surplus-lines/opportunities/latest`;
    return this._https.get(url).pipe(
      tap(response => localStorage.setItem('CompassOpportunity', JSON.stringify(response))),
      map(response => response as SurplusLinesOpportunityDetail)
    );
  }

  getHasQuotes(Email: string): Observable<Boolean> {
    const url = `${environment.apiUrl}api/surplus-lines/token/checkQuotes`;
    const Body = { Email };
    return this._https.post(url, Body).pipe(
      map(response => response as Boolean)
    );
  }

  useIPHLToken(applicantInfo: SurplusLinesApplicant): Observable<CompassQuoteOptions> {
    const url = `${environment.apiUrl}api/surplus-lines/token/use`;
    return this._https.post(url, applicantInfo).pipe(
      tap(() => localStorage.setItem('ApplicantInfo', JSON.stringify(applicantInfo))),
      tap(response => localStorage.setItem('QuoteOptions', JSON.stringify(response))),
      map(response => response as CompassQuoteOptions)
    );
  }

  returnIPHLToken(applicantInfo: SurplusLinesApplicant): Observable<CompassQuoteOptions> {
    const url = `${environment.apiUrl}api/surplus-lines/token/return`;
    return this._https.post(url, applicantInfo).pipe(
      tap(() => localStorage.setItem('ApplicantInfo', JSON.stringify(applicantInfo))),
      tap(response => localStorage.setItem('QuoteOptions', JSON.stringify(response))),
      tap(() => this.saveReturningUser()),
      map(response => response as CompassQuoteOptions)
    );
  }

  getApplicantInfo(userId = this.CurrentUser.Id): Observable<SurplusLinesApplicant> {
    const url = `${environment.apiUrl}api/surplus-lines/get/applicant/${userId}`;
    return this._https.get(url).pipe(
      tap(response => localStorage.setItem('ApplicantInfo', JSON.stringify(response))),
      map(response => response as SurplusLinesApplicant)
    );
  }

  UpdateCompassApplicantInfo(applicantInfo: SurplusLinesApplicant): Observable<SurplusLinesApplicant> {
    const url = `${environment.apiUrl}api/surplus-lines/applicants`;
    return this._https.put(url, applicantInfo).pipe(
      map(response => response as SurplusLinesApplicant)
    );
  }

  getApplicantById(applicantId: number): Observable<SurplusLinesApplicant> {
    const url = `${environment.apiUrl}api/surplus-lines/get/applicantId/${applicantId}`;
    return this._https.get(url).pipe(
      tap(response => localStorage.setItem('ApplicantInfo', JSON.stringify(response))),
      map(response => response as SurplusLinesApplicant)
    );
  }

  deleteApplication(id: number): Observable<boolean> {
    const url = `${environment.apiUrl}api/surplus-lines/snapshots`;
    const params = new HttpParams().set('AppId', id);
    return this._https.delete(url, { params }).pipe(
      map(response => response as boolean)
    );
  }
  isReturningUser(): boolean {
    const fromStorage = localStorage.getItem('isReturningUser');

    return Boolean(fromStorage ? JSON.parse(fromStorage) : false);
  }

  saveReturningUser(): void {
    localStorage.setItem('isReturningUser', JSON.stringify(true));
  }
}
